import React, { useEffect, useState } from 'react'
import { Container, Badge, CircularProgress, Box, Heading, Text, Textarea, Button, Alert, Stack } from '@chakra-ui/react'
import { useParams } from 'react-router-dom'
import useTitle from '../hooks/useTitle'
import api from '../api'
import useStore from '../store'

import Terms from './includes/Terms'
import RichTextViewer from './includes/RichTextViewer'

import ShortText from './fields/ShortText'
import LongText from './fields/LongText'
import Integer from './fields/Integer'
import Float from './fields/Float'
import Slider from './fields/Slider'
import Choice from './fields/Choice'
import DateInput from './fields/Date'
import DatetimeInput from './fields/Datetime'
import Temperature from './fields/Temperature'
import Height from './fields/Height'
import Weight from './fields/Weight'
import BMI from './fields/BMI'

export const POLL_FIELDS = {
  text: ShortText,
  paragraph: LongText,
  integer: Integer,
  float: Float,
  slider: Slider,
  choice: Choice,
  date: DateInput,
  datetime: DatetimeInput,
  temperature: Temperature,
  height: Height,
  weight: Weight,
  bmi: BMI
}

export default function Poll () {
  const [trial, setTrial] = useState(null)
  const [stage, setStage] = useState(null)
  const [responses, setResponses] = useState({})
  const [postSubmissionText, setPostSubmissionText] = useState('')
  const [termsAgreed, setTermsAgreed] = useState(false)
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')
  const [errorComponent, setErrorComponent] = useState('')
  const [loading, setLoading] = useState(false)
  const { stageId, linkId } = useParams()
  const { setTeam } = useStore()
  useTitle(stage ? stage.name : 'Poll')

  useEffect(() => {
    if (!stageId) setError('No poll could be found.')
    else {
      setLoading(true)
      api.stages.getPoll(stageId, data => {
        setStage(data)
        const team = data.ownerTeam
        if (data?.ownerTrial?.sponsor) team.name = data.ownerTrial.sponsor
        if (data?.ownerTrial?.sponsorLogoUrl) team.logoUrl = data.ownerTrial.sponsorLogoUrl
        setTrial(data.ownerTrial)
        setTeam(team)
        setLoading(false)
      }, err => {
        setError(`There was an error fetching the poll: ${err.message}`)
        setLoading(false)
      })
    }
  }, [stageId])

  function submit () {
    if (!termsAgreed) return setError('You need to agree to the terms and conditions in order to submit your data.')
    setError()
    setErrorComponent()
    api.stages.submitPollData(stage._id, { termsAgreed, response: responses, accessId: linkId }, data => {
      setSuccess(true)
      setPostSubmissionText(data.postSubmissionText)
    }, err => {
      let message = err.message || ''
      if (message?.indexOf('[') > -1) {
        const match = err.message.match(/\[([a-z0-9-]+)\]/i)
        setErrorComponent(match[1])
        message = message.replace(match[0], '')
      }
      setError(message)
    })
  }

  function conditionIsMet (c) {
    if (!c.condition) return true
    const conditionResp = responses[c.condition.dataType]?.value

    const exists =
      conditionResp !== null && conditionResp !== undefined && conditionResp !== '' &&
      (Array.isArray(conditionResp) ? (conditionResp?.length > 0) : true)

    const isEqual =
      conditionResp?.toString()?.toLowerCase()?.trim() === c.condition.value?.toString()?.toLowerCase()?.trim() ||
      (Array.isArray(c.condition?.value) && c.condition?.value.indexOf(conditionResp) > -1) ||
      (Array.isArray(conditionResp) && conditionResp.indexOf(c.condition?.value) > -1) ||
      (Array.isArray(c.condition?.value) && Array.isArray(conditionResp) && c.condition.value.filter(x => conditionResp?.indexOf(x) !== -1)?.length > 0)

    return (
      (c.condition.operator === 'exists' && exists) ||
      (c.condition.operator === 'equal' && isEqual) ||
      (c.condition.operator === 'notequal' && !isEqual) ||
      (c.condition.operator === 'more' && conditionResp > c.condition.value) ||
      (c.condition.operator === 'less' && conditionResp < c.condition.value)
    )
  }

  return (
    <Container mt={5} mb={5}>

      <Heading size='lg'>Welcome</Heading>
      {loading &&
        <Box mt={5} display='flex' justifyContent='center'>
          <CircularProgress isIndeterminate color='primary.300' />
        </Box>}

      {stage &&
        <Box mt={5}>
          <Heading size='md'>{stage.name}</Heading>
          <Text color='gray.500' mb={5}>{stage.description}</Text>

          {success
            ? (
              <Box>
                <Alert variant='top-accent' status='success' mb={5} flexDirection='column'>
                  <Heading size='sm'>Thank you</Heading>
                  Your response has been submitted successfully.
                </Alert>
                {postSubmissionText && <RichTextViewer content={postSubmissionText} />}
              </Box>)
            : (
              <Box>
                <Stack spacing={5}>
                  {stage.components?.map(component => {
                    if (!conditionIsMet(component)) return null
                    if (component.type === 'title') {
                      return (
                        <Box key={component.id} p={2} bg='blue.50'>
                          <Heading key={component.id} size='md'>{component.title}</Heading>
                        </Box>
                      )
                    }
                    if (component.type === 'sectionDivider') {
                      return (
                        <Box key={component.id} p={2} bg='blue.50'>
                          <Heading size='md'>{component.title}</Heading>
                          <RichTextViewer content={component.description} />
                        </Box>
                      )
                    }
                    const Field = POLL_FIELDS[component.type?.type]
                    const value = responses[component.dataType]?.value
                    const onChange = v => setResponses({
                      ...responses,
                      [component.dataType]: {
                        value: v,
                        createdAt: new Date()
                      }
                    })
                    return (
                      <Box key={component.id} p={3} borderWidth='1px' rounded='md' bg={errorComponent === component.id ? 'red.100' : 'gray.50'}>
                        {component.isRequired &&
                          <Box mb={1} display='flex' justifyContent='flex-end'>
                            <Badge colorScheme='red'>Required</Badge>
                          </Box>}

                        <Heading as='h3' size='md'>{component.title}</Heading>
                        <RichTextViewer content={component.description} />
                        <Box mt={3}>
                          {Field
                            ? <Field dataType={component.type} value={value} onChange={onChange} />
                            : <Alert variant='left-accent'>This question type is not yet supported in polls.</Alert>}
                        </Box>
                      </Box>
                    )
                  })}
                </Stack>

                <Box mt={10} p={3} bg='blue.100' rounded='md'>
                  <Heading size='md'>Conditions & privacy</Heading>

                  {trial?.conditions && (
                    <Box mt={3} mb={1}>
                      <Text>The trial coordinator has provided the following conditions and privacy statement.</Text>
                      <Textarea readOnly bg='white' rows={8} value={trial.conditions} />
                    </Box>
                  )}

                  <Terms value={termsAgreed} onChange={setTermsAgreed} agreementStatement='I have read and I agree to the trial conditions. I have also read and I agree to the Trialflare conditions and privacy statement' />
                </Box>

                {error && <Alert variant='left-accent' status='error' mt={10}>{error}</Alert>}
                <Button w='100%' mt={10} colorScheme='primary' onClick={submit}>Submit</Button>
              </Box>)}
        </Box>}

    </Container>
  )
};
